import pick from 'lodash.pick';
import { batchActions } from 'redux-batched-actions';
import _ from 'underscore';

import { subscribeFaye } from './faye';
import http from './http';

import {
    showErrorNotification,
    setShouldScrollToBottom,
    setFetchingMoreMessages as setFetchingMoreMessagesUi,
    showConnectNotification,
    setActiveConversationId,
    cleanTypingIndicatorEvents,
} from '../actions/app-state';
import { setAuth } from '../actions/auth';
import { setConfig } from '../actions/config';
import { linkEmailChannel } from '../actions/integrations';
import { cancelTyping } from '../actions/typing';
import { setUser, immediateUpdate as immediateUpdateUser, resetPendingUserProps } from '../actions/user';
import * as ConversationActions from '../actionTypes/conversation';

import { observable } from '../utils/events';
import { delegate } from '../utils/delegate';
import { resizeImage, getBlobFromDataUrl } from '../utils/media';
import { getClientId, getClientInfo, getSessionId, verifyStoredClientId } from '../utils/client';
import * as storage from '../utils/storage';
import { hasLinkableChannels, getLinkableChannels, isChannelLinked } from '../utils/user';
import { getWindowLocation } from '../utils/dom';
import { getUploadError, getFileTooLargeText } from '../utils/errors';
import { filterClientProps } from '../utils/message';
import * as rollbarLib from '../utils/rollbar';
import { CONNECT_NOTIFICATION_DELAY_IN_SECONDS } from '../constants/notifications';
import { SEND_STATUS, LOCATION_ERRORS, MAX_UPLOAD_FILE_SIZE } from '../constants/message';
import { PRECHAT_CAPTURE } from '../constants/intent';
import { CHANNEL_CATEGORIES } from '../constants/channels';
import { PRECHAT_CAPTURE_ERROR, PRECHAT_CAPTURE_MESSAGE_DELAY_MS } from '../constants/prechat-capture';

export const POST_MESSAGE_ATTRIBUTES = [
    'type',
    'text',
    'name',
    'coordinates',
    'avatarUrl',
    'metadata',
    'payload',
    'mediaUrl',
    'mediaType',
    'role',
    'fields',
    'quotedMessageId',
];

export function setAppMakerLastRead(conversationId, appMakerLastRead) {
    return {
        type: ConversationActions.SET_APP_MAKER_LAST_READ,
        conversationId,
        appMakerLastRead,
    };
}

export function resetConversation() {
    return {
        type: ConversationActions.RESET_CONVERSATION,
    };
}

export function addParticipant(conversationId, participant) {
    return {
        type: ConversationActions.ADD_PARTICIPANT,
        conversationId,
        participant,
    };
}

export function removeParticipant(conversationId, participant) {
    return {
        type: ConversationActions.REMOVE_PARTICIPANT,
        conversationId,
        participant,
    };
}

export function setParticipantLastRead(conversationId, participantData) {
    return {
        type: ConversationActions.SET_PARTICIPANT_LAST_READ,
        conversationId,
        appUserId: participantData.appUserId,
        lastRead: participantData.lastRead,
    };
}

export function setConversation(props) {
    return {
        type: ConversationActions.SET_CONVERSATION,
        conversation: props,
    };
}

export function setConversations(conversations) {
    return {
        type: ConversationActions.SET_CONVERSATIONS,
        conversations,
    };
}

export function addMessage(conversationId, message) {
    return {
        type: ConversationActions.ADD_MESSAGE,
        conversationId,
        message,
    };
}

export function addCompletedUpload(conversationId, message) {
    return {
        type: ConversationActions.ADD_COMPLETED_UPLOAD,
        message,
        conversationId,
    };
}

export function removeCompletedUpload(conversationId, queryProps) {
    return {
        type: ConversationActions.REMOVE_COMPLETED_UPLOAD,
        queryProps: {
            ...queryProps,
        },
        conversationId,
    };
}

export function addMessages(conversationId, messages) {
    return {
        type: ConversationActions.ADD_MESSAGES,
        conversationId,
        messages,
    };
}

export function rejectMessage(conversationId, queryProps, error) {
    return {
        type: ConversationActions.REJECT_MESSAGE,
        queryProps,
        error,
        conversationId,
    };
}

export function replaceMessage(conversationId, queryProps, message) {
    return {
        type: ConversationActions.REPLACE_MESSAGE,
        queryProps,
        message,
        conversationId,
    };
}

export function incrementUnreadCount(conversationId) {
    return {
        type: ConversationActions.INCREMENT_UNREAD_COUNT,
        conversationId,
    };
}

export function setFetchingMoreMessagesFromServer(conversationId, value) {
    return {
        type: ConversationActions.SET_FETCHING_MORE_MESSAGES_FROM_SERVER,
        value,
        conversationId,
    };
}

export function setLastReceivedMessageId(conversationId, value) {
    return {
        type: ConversationActions.SET_LAST_RECEIVED_MESSAGE_ID,
        value,
        conversationId,
    };
}

export function setCompletedPrechatCapture(conversationId, value) {
    return {
        type: ConversationActions.SET_COMPLETED_PRECHAT_CAPTURE,
        value,
        conversationId,
    };
}

export function setCompletedPrechatCaptureLinking(conversationId, value) {
    return {
        type: ConversationActions.SET_COMPLETED_PRECHAT_CAPTURE_LINKING,
        value,
        conversationId,
    };
}

export function setLatestAppUserMessageTimestamp(conversationId, value) {
    return {
        type: ConversationActions.SET_LATEST_APPUSER_MESSAGE_TIMESTAMP,
        value,
        conversationId,
    };
}

export function setMessages(conversationId, messages) {
    return {
        type: ConversationActions.SET_MESSAGES,
        conversationId,
        messages,
    };
}

export function mergeMessages(conversationId, messages) {
    return {
        type: ConversationActions.MERGE_MESSAGES,
        conversationId,
        messages,
    };
}

export function refreshConversations(conversations, options) {
    return {
        type: ConversationActions.REFRESH_CONVERSATIONS,
        conversations,
        keepConvoWithFetchedMessages: options.keepConvoWithFetchedMessages,
    };
}

function postSendMessage(message, conversationId) {
    return (dispatch, getState) => {
        const {
            config: { appId, integrationId },
            user: { _id: appUserId },
            conversations,
        } = getState();
        const conversation = conversations[conversationId];

        const completedPrechatCapture = conversation && !!conversation.completedPrechatCapture;
        const body = {
            message: pick(message, POST_MESSAGE_ATTRIBUTES),
            author: {
                role: 'appUser',
                appUserId,
                client: getClientInfo(integrationId),
                sessionId: getSessionId(),
            },
        };

        if (completedPrechatCapture) {
            body.intent = PRECHAT_CAPTURE.REPLY;
        }

        return dispatch(http('POST', `/v2/apps/${appId}/conversations/${conversationId}/messages`, body));
    };
}

function postUploadFile(message) {
    return (dispatch, getState) => {
        const {
            config: { appId, integrationId },
            user: { _id: appUserId },
            appState: { activeConversationId },
        } = getState();
        const blob = message._blob;
        delete message._blob;
        const data = new FormData();

        data.append(
            'author',
            JSON.stringify({
                role: 'appUser',
                appUserId,
                client: getClientInfo(integrationId),
                sessionId: getSessionId(),
            }),
        );

        if (message.metadata) {
            data.append('message', JSON.stringify(pick(message, ['metadata'])));
        }

        data.append('source', blob, message._fileName);

        return dispatch(http('POST', `/apps/${appId}/conversations/${activeConversationId}/files`, data));
    };
}

function onUploadComplete(message, { messageId }) {
    return (dispatch, getState) => {
        const {
            conversations,
            appState: { activeConversationId },
        } = getState();
        const conversation = conversations[activeConversationId];
        const { rejectedMessages, completedUploads } = conversation;

        const completedUpload = completedUploads.find((m) => m._id === messageId);
        const messageAlreadyRejected = Object.keys(rejectedMessages).includes(messageId);

        if (messageAlreadyRejected) {
            const error = rejectedMessages[messageId];
            dispatch(onUploadFailed(message, error));
        } else if (completedUpload) {
            dispatch(
                removeCompletedUpload(activeConversationId, {
                    _id: completedUpload._id,
                }),
            );

            dispatch(completeUpload(message, completedUpload));
        } else {
            dispatch(
                replaceMessage(
                    activeConversationId,
                    {
                        _clientId: message._clientId,
                    },
                    Object.assign(message, {
                        _id: messageId,
                    }),
                ),
            );
        }
    };
}

function onUploadFailed(message, error) {
    return (dispatch, getState) => {
        const {
            ui: { text },
            appState: { activeConversationId },
        } = getState();
        const { shouldRemoveMessage, bannerText } = getUploadError(error, text);

        if (shouldRemoveMessage) {
            dispatch(showErrorNotification(bannerText));
            dispatch(
                removeMessage(activeConversationId, {
                    _clientId: message._clientId,
                }),
            );
        } else {
            dispatch(onMessageSendFailure(message, activeConversationId));
        }
    };
}

export function completeUpload(existingMessage, completedUpload, conversationId) {
    return (dispatch, getState) => {
        const isActiveConversation = conversationId === getState().appState.activeConversationId;
        return Promise.resolve(
            dispatch(onMessageSendSuccess(existingMessage, completedUpload, conversationId)),
        ).then(() => (isActiveConversation ? dispatch(handleConnectNotification()) : null));
    };
}

function onMessageSendSuccess(message, sentMessage, conversationId) {
    return (dispatch, getState) => {
        const isActiveConversation = conversationId === getState().appState.activeConversationId;
        const actions = [];

        if (isActiveConversation) {
            actions.push(setShouldScrollToBottom(true));
        }
        actions.push(
            replaceMessage(
                conversationId,
                {
                    _clientId: message._clientId,
                },
                sentMessage,
            ),
        );
        actions.push(setCompletedPrechatCapture(conversationId, false));

        dispatch(batchActions(actions));

        observable.trigger('message:sent', filterClientProps(sentMessage), { conversation: { _id: conversationId } });
    };
}

function onMessageSendFailure(message, conversationId) {
    return (dispatch, getState) => {
        const isActiveConversation = conversationId === getState().appState.activeConversationId;
        const actions = [];
        message.sendStatus = SEND_STATUS.FAILED;

        if (isActiveConversation) {
            actions.push(setShouldScrollToBottom(true));
        }
        actions.push(
            replaceMessage(
                conversationId,
                {
                    _clientId: message._clientId,
                },
                message,
            ),
        );

        dispatch(batchActions(actions));
    };
}

function addClientMessage(props, conversationId) {
    return (dispatch, getState) => {
        const {
            appState: { activeConversationId },
            config: { integrationId },
            conversations,
            user,
        } = getState();
        const conversation = conversations[conversationId];
        const isActiveConversation = conversationId === activeConversationId;

        const message = {
            type: 'text',
            role: 'appUser',
            authorId: user._id,
            _clientId: Math.random(),
            _clientSent: Date.now() / 1000,
            sendStatus: SEND_STATUS.SENDING,
            source: {
                id: getClientId(integrationId),
            },
        };

        if (typeof props === 'string') {
            message.text = props;
        } else {
            Object.assign(message, props);
        }

        if (isActiveConversation || (conversation && conversation.messagesUpdated)) {
            if (props._clientId) {
                const oldMessage = conversation.messages.find((m) => m._clientId === props._clientId);
                const newMessage = Object.assign({}, oldMessage, props);

                dispatch(
                    replaceMessage(
                        conversationId,
                        {
                            _clientId: props._clientId,
                        },
                        newMessage,
                    ),
                );

                return newMessage;
            }

            const actions = [];

            if (conversationId === activeConversationId) {
                actions.push(setShouldScrollToBottom(true));
            }

            actions.push(addMessage(conversationId, message));

            dispatch(batchActions(actions));
        }

        return message;
    };
}

export function removeMessage(conversationId, queryProps) {
    return (dispatch, getState) => {
        const actions = [];

        if (conversationId === getState().appState.activeConversationId) {
            actions.push(setShouldScrollToBottom(true));
        }

        actions.push({
            type: ConversationActions.REMOVE_MESSAGE,
            conversationId,
            queryProps: {
                ...queryProps,
            },
        });

        dispatch(batchActions(actions));
    };
}

function _getMessages({ before, conversationId } = {}) {
    return (dispatch, getState) => {
        const {
            config: { appId },
            appState: { activeConversationId },
        } = getState();
        const targetConversationId = conversationId || activeConversationId;

        const data = {};
        if (before) {
            data.before = before;
        }

        return dispatch(http('GET', `/v2/apps/${appId}/conversations/${targetConversationId}/messages`, data));
    };
}

function sendChain(
    sendFn,
    message,
    conversationId,
    { postSendHandler, postFailHandler, onErrorCallback, onSuccessCallback } = {},
) {
    return (dispatch, getState) => {
        cancelTyping();

        return dispatch(startConversation('message:appUser'))
            .then((response) => dispatch(immediateUpdateUser()).then(() => response))
            .then(({ conversations }) => {
                const targetConversationId = conversationId === 'default' ? conversations[0]._id : conversationId;
                const state = getState();
                const isActiveConversation = targetConversationId === state.appState.activeConversationId;
                const conversation = state.conversations[targetConversationId];
                const messageToSend = delegate.beforeSend(message, conversation);

                if (!postSendHandler) {
                    postSendHandler = (response) =>
                        Promise.resolve(
                            dispatch(onMessageSendSuccess(message, response.messages[0], targetConversationId)),
                        ).then(() => (isActiveConversation ? dispatch(handleConnectNotification()) : undefined));
                }

                if (onSuccessCallback) {
                    const firstSendHandler = postSendHandler;
                    postSendHandler = (response) => Promise.resolve(firstSendHandler(response)).then(onSuccessCallback);
                }

                if (!postFailHandler) {
                    postFailHandler = (error) => dispatch(onMessageSendFailure(message, error, targetConversationId));
                }

                if (onErrorCallback) {
                    const firstFailHandler = postFailHandler;
                    postFailHandler = (error) => Promise.resolve(firstFailHandler(error)).then(onErrorCallback);
                }

                return dispatch(sendFn(messageToSend, targetConversationId))
                    .then(postSendHandler)
                    .catch(postFailHandler);
            });
    };
}

export function sendMessage(props, { callbacks, conversationId } = {}) {
    return (dispatch, getState) => {
        const targetConversationId = conversationId || getState().appState.activeConversationId;
        const message = dispatch(addClientMessage(props, targetConversationId));
        return dispatch(sendChain(postSendMessage, message, targetConversationId, callbacks));
    };
}

export function postPostback(postback, options = {}) {
    return (dispatch, getState) => {
        const { shouldShowErrorNotification = true, conversationId } = options;
        const {
            config: { appId, integrationId },
            user: { _id: appUserId },
            appState: { activeConversationId },
            ui,
        } = getState();
        const targetConversationId = conversationId || activeConversationId;

        return dispatch(
            http('POST', `/v2/apps/${appId}/conversations/${targetConversationId}/postback`, {
                ...postback,
                author: {
                    appUserId,
                    role: 'appUser',
                    client: getClientInfo(integrationId),
                },
            }),
        ).catch((e) => {
            if (shouldShowErrorNotification) {
                return dispatch(showErrorNotification(ui.text.actionPostbackError));
            }
            throw e;
        });
    };
}

export function sendPostback(actionId, conversationId) {
    return (dispatch, getState) => {
        const {
            appState: { activeConversationId },
            conversations,
        } = getState();
        const targetConversationId = conversationId || activeConversationId;

        const conversation = conversations[targetConversationId];
        const postback = {
            postback: {
                actionId,
            },
        };

        const postbackToSend = delegate.beforePostbackSend(postback, conversation);
        const shouldShowErrorNotification = targetConversationId === activeConversationId;

        return dispatch(postPostback(postbackToSend, { conversationId, shouldShowErrorNotification }));
    };
}

export function fetchMoreMessages() {
    return (dispatch, getState) => {
        const {
            appState: { activeConversationId },
            conversations,
        } = getState();
        const { hasMoreMessages, messages, isFetchingMoreMessagesFromServer } = conversations[activeConversationId];
        if (!hasMoreMessages || isFetchingMoreMessagesFromServer) {
            return Promise.resolve();
        }

        const timestamp = messages[0].received;
        dispatch(setFetchingMoreMessagesFromServer(activeConversationId, true));

        return dispatch(
            _getMessages({
                before: timestamp,
            }),
        ).then((response) => {
            const actions = [
                setConversation({
                    _id: activeConversationId,
                    hasMoreMessages: !!response.hasPrevious,
                }),
                addMessages(activeConversationId, response.messages),
                setFetchingMoreMessagesFromServer(activeConversationId, false),
                setFetchingMoreMessagesUi(false),
            ];

            dispatch(batchActions(actions));
            return response;
        });
    };
}

export function handleConnectNotification({ force } = {}) {
    return (dispatch, getState) => {
        const dispatchShowConnectNotification = (options) => {
            dispatch(batchActions([showConnectNotification(options), setShouldScrollToBottom(true)]));
        };

        const {
            user: { clients },
            config: { integrations },
            conversations,
            ui: { isConnectNotificationAllowed },
            appState: { activeConversationId: conversationId },
        } = getState();

        const conversation = conversations[conversationId];
        const { messages, completedPrechatCaptureLinking, isDefault } = conversation;

        if ((!force && !isConnectNotificationAllowed) || !isDefault) {
            return;
        }

        const appUserMessages = messages.filter((message) => message.role === 'appUser');

        const channelsAvailable = hasLinkableChannels(integrations, clients);
        const hasSomeChannelLinked = getLinkableChannels(integrations).some((channel) => {
            // if it's a category, let's check individual channel types
            // as `isChannelLinked` accepts a specific type
            if (CHANNEL_CATEGORIES[channel]) {
                return CHANNEL_CATEGORIES[channel].some((type) => isChannelLinked(clients, type));
            }

            return isChannelLinked(clients, channel);
        });

        if (channelsAvailable && !hasSomeChannelLinked) {
            /*
             * we want to show a connect notification when the appMaker has received the first message.
             * if the appUser sends multiple messages in quick succession, appUserMessages will have
             * length > 1, but will only have one message with the `received` property
             */
            const receivedMessages = appUserMessages.filter(({ received }) => received);
            const isFirstAppUserInteraction = receivedMessages.length === 1;

            if (isFirstAppUserInteraction) {
                const lastAppUserMessage = _.last(appUserMessages);
                // we want a slight delay in the case that the user is also receiving an office hours away message
                setTimeout(
                    () =>
                        dispatchShowConnectNotification(
                            (lastAppUserMessage._clientSent || lastAppUserMessage.received) + 1,
                        ),
                    PRECHAT_CAPTURE_MESSAGE_DELAY_MS,
                );
            } else {
                // find the last confirmed message timestamp
                let lastMessageTimestamp;

                // start at -2 to ignore the message that was just sent
                for (let index = appUserMessages.length - 2; index >= 0 && !lastMessageTimestamp; index--) {
                    const message = appUserMessages[index];
                    lastMessageTimestamp = message.received;
                }

                if (force || lastMessageTimestamp) {
                    // divide it by 1000 since server `received` is in seconds and not in ms
                    const currentTimeStamp = Date.now() / 1000;
                    if (force || currentTimeStamp - lastMessageTimestamp >= CONNECT_NOTIFICATION_DELAY_IN_SECONDS) {
                        dispatchShowConnectNotification(currentTimeStamp);
                    }
                }
            }
        } else if (completedPrechatCaptureLinking && appUserMessages.length === 1) {
            // we want a slight delay in the case that the user is also receiving an office hours away message
            setTimeout(
                () =>
                    dispatchShowConnectNotification(
                        (appUserMessages[0]._clientSent || appUserMessages[0].received) + 1,
                    ),
                PRECHAT_CAPTURE_MESSAGE_DELAY_MS,
            );
        }
    };
}

export function resetUnreadCount(convoId) {
    return (dispatch, getState) => {
        const {
            user: { _id: appUserId },
            config: { appId, integrationId },
            conversations,
            appState: { activeConversationId },
        } = getState();
        const conversationId = convoId || activeConversationId;
        const conversation = conversations[conversationId];

        if (conversationId === 'default') {
            return;
        }

        if (!conversation || conversation.unreadCount > 0) {
            dispatch({
                type: ConversationActions.RESET_UNREAD_COUNT,
                conversationId,
            });

            return dispatch(
                http('POST', `/v2/apps/${appId}/conversations/${conversationId}/activity`, {
                    activity: {
                        type: 'conversation:read',
                    },
                    author: {
                        appUserId,
                        role: 'appUser',
                        client: getClientInfo(integrationId),
                    },
                }),
            ).then((response) => {
                return response;
            });
        }

        return Promise.resolve();
    };
}

export function resendMessage(messageClientId, { callbacks, failedMessagePredicate } = {}) {
    return (dispatch, getState) => {
        const {
            conversations,
            appState: { activeConversationId },
        } = getState();
        const conversation = conversations[activeConversationId];
        const oldMessage = conversation.messages.find(
            failedMessagePredicate || ((message) => message._clientId === messageClientId),
        );

        if (!oldMessage) {
            return;
        }

        const newMessage = Object.assign({}, oldMessage, {
            sendStatus: SEND_STATUS.SENDING,
        });

        dispatch(
            replaceMessage(
                activeConversationId,
                {
                    _clientId: messageClientId || oldMessage._clientId,
                },
                newMessage,
            ),
        );

        if (newMessage.type === 'text') {
            return dispatch(sendChain(postSendMessage, newMessage, activeConversationId));
        } else if (newMessage.type === 'location') {
            if (newMessage.coordinates) {
                return dispatch(sendChain(postSendMessage, newMessage, activeConversationId));
            } else {
                return dispatch(sendLocation(newMessage));
            }
        } else if (newMessage.type === 'formResponse') {
            return dispatch(sendChain(postSendMessage, newMessage, activeConversationId, callbacks));
        }

        return dispatch(
            sendChain(postUploadFile, newMessage, activeConversationId, {
                postSendHandler: (response) => {
                    dispatch(onUploadComplete(newMessage, response));
                },
                postFailHandler: (error) => {
                    dispatch(onUploadFailed(newMessage, error));
                },
            }),
        );
    };
}

export function sendLocation(props = {}) {
    return (dispatch, getState) => {
        const {
            ui,
            appState: { activeConversationId },
        } = getState();
        let message;

        if (props._clientSent) {
            message = props;
        } else {
            message = dispatch(
                addClientMessage(
                    {
                        type: 'location',
                        ...props,
                    },
                    activeConversationId,
                ),
            );
        }

        if (message.coordinates) {
            return dispatch(sendChain(postSendMessage, message, activeConversationId));
        }

        const locationServicesDeniedText = ui.text.locationServicesDenied;
        const locationSecurityRestrictionText = ui.text.locationSecurityRestriction;

        return new Promise((resolve) => {
            let timedOut = false;

            const timeout = setTimeout(() => {
                timedOut = true;
                dispatch(onMessageSendFailure(message, activeConversationId));
                resolve();
            }, 15000);

            navigator.geolocation.getCurrentPosition(
                (position) => {
                    clearTimeout(timeout);
                    if (timedOut) {
                        return;
                    }

                    Object.assign(message, {
                        coordinates: {
                            lat: position.coords.latitude,
                            long: position.coords.longitude,
                        },
                    });

                    dispatch(
                        replaceMessage(
                            activeConversationId,
                            {
                                _clientId: message._clientId,
                            },
                            message,
                        ),
                    );

                    dispatch(sendChain(postSendMessage, message, activeConversationId)).then(resolve);
                },
                (err) => {
                    clearTimeout(timeout);
                    if (timedOut) {
                        return;
                    }
                    if (getWindowLocation().protocol !== 'https:') {
                        setTimeout(() => alert(locationSecurityRestrictionText), 100);
                        dispatch(
                            removeMessage(activeConversationId, {
                                _clientId: message._clientId,
                            }),
                        );
                    } else if (err.code === LOCATION_ERRORS.PERMISSION_DENIED) {
                        setTimeout(() => alert(locationServicesDeniedText), 100);
                        dispatch(
                            removeMessage(activeConversationId, {
                                _clientId: message._clientId,
                            }),
                        );
                    } else {
                        dispatch(onMessageSendFailure(message, activeConversationId));
                    }
                    resolve();
                },
                {
                    timeout: 15000,
                },
            );
        });
    };
}

function _getFileDataUrl(file) {
    return new Promise((resolve, reject) => {
        const reader = new FileReader();

        if (file) {
            reader.readAsDataURL(file);
        }

        reader.addEventListener('load', function () {
            resolve(reader.result);
        });

        reader.addEventListener('error', function (err) {
            reject(err);
        });
    });
}

function _getDataUrl(file) {
    const isImage = file.type && file.type.startsWith('image/');
    const isGif = file.type === 'image/gif';

    if (isImage && !isGif) {
        return new Promise((resolve, reject) => {
            resizeImage(file)
                .then(resolve)
                .catch(() => {
                    _getFileDataUrl(file).then(resolve).catch(reject);
                });
        });
    } else {
        return _getFileDataUrl(file);
    }
}

export function uploadFile(file) {
    return (dispatch, getState) => {
        const isImage = file.type && file.type.startsWith('image/');

        if (file.size > MAX_UPLOAD_FILE_SIZE.VALUE) {
            const errorMessage = getFileTooLargeText(getState().ui.text);
            return dispatch(showErrorNotification(errorMessage));
        }

        return _getDataUrl(file).then((dataUrl) => {
            const activeConversationId = getState().appState.activeConversationId;
            const imageBlob = getBlobFromDataUrl(dataUrl, file.type);
            const blobUrl = URL.createObjectURL(imageBlob);
            const message = dispatch(
                addClientMessage(
                    {
                        _blob: imageBlob,
                        _fileName: file.name,
                        mediaType: file.type,
                        mediaSize: file.size,
                        mediaUrl: isImage ? blobUrl : file.name,
                        type: isImage ? 'image' : 'file',
                    },
                    activeConversationId,
                ),
            );

            return dispatch(
                sendChain(postUploadFile, message, activeConversationId, {
                    postSendHandler: (response) => {
                        dispatch(onUploadComplete(message, response));
                    },
                    postFailHandler: (error) => {
                        dispatch(onUploadFailed(message, error));
                    },
                }),
            );
        });
    };
}

const setUserForConversation = (appUser, sessionToken, integrationId) => {
    if (rollbarLib.client() && rollbarLib.get('user_id') !== appUser._id) {
        rollbarLib.client().configure({
            payload: {
                user_id: appUser._id,
            },
        });
    }

    if (appUser.userId) {
        // if the user is identified using a userId,
        // there's no need to persist or leave the appUserId
        // in storage since it's useless without a jwt
        storage.removeItem(`${integrationId}.appUserId`);
    } else {
        storage.setItem(`${integrationId}.appUserId`, appUser._id);
    }

    if (sessionToken) {
        storage.setItem(`${integrationId}.sessionToken`, sessionToken);
    }
};

const getUserActions = (appUser, settings, sessionToken) => {
    const actions = [setUser(appUser)];

    for (const key of Object.keys(settings)) {
        actions.push(setConfig(key, settings[key]));
    }

    if (sessionToken) {
        actions.push(
            setAuth({
                sessionToken,
            }),
        );
    }

    return actions;
};

export function handleUserConversationsResponse(
    { appUser, conversations = [], settings = {}, sessionToken, completedPrechatCapture = false },
    { shouldFetchMessages = true } = {},
) {
    return (dispatch, getState) => {
        dispatch(batchActions(getUserActions(appUser, settings, sessionToken)));

        const actions = [];

        if (conversations.length > 0) {
            actions.push(setConversations(conversations));

            actions.push(setActiveConversationId(conversations[0]._id));
            actions.push(cleanTypingIndicatorEvents());

            if (completedPrechatCapture) {
                actions.push(setCompletedPrechatCapture(conversations[0]._id, completedPrechatCapture));
            }
        }

        const {
            config: { integrationId },
        } = getState();
        setUserForConversation(appUser, sessionToken, integrationId);

        function dispatchActionsAndSubscribe() {
            dispatch(batchActions(actions));

            if (appUser._id) {
                subscribeFaye();
            }

            return Promise.resolve({ conversations });
        }

        if (shouldFetchMessages && conversations.length > 0) {
            const conversationIdToLoad = conversations[0]._id;
            return dispatch(_getMessages({ conversationId: conversationIdToLoad })).then((response) => {
                actions.push(setConversation({ _id: conversationIdToLoad, hasMoreMessages: !!response.hasPrevious }));
                actions.push(setMessages(conversationIdToLoad, response.messages));

                return dispatchActionsAndSubscribe();
            });
        }

        return dispatchActionsAndSubscribe();
    };
}

export function handleUserConversationResponse({
    appUser,
    conversation,
    hasPrevious,
    messages = [],
    sessionToken,
    settings = {},
    completedPrechatCapture = false,
}) {
    return (dispatch, getState) => {
        const actions = getUserActions(appUser, settings, sessionToken);
        const {
            config: { integrationId },
        } = getState();

        if (conversation && conversation._id) {
            const conversationActions = _.compact([
                setConversation({
                    ...conversation,
                    hasMoreMessages: !!hasPrevious,
                }),
                setActiveConversationId(conversation._id),
                cleanTypingIndicatorEvents(),
                // fix case where there's no conversation, should not set messages because it will override the messages temporarily put in default conversation
                messages.length ? setMessages(conversation._id, messages) : null,
                setCompletedPrechatCapture(conversation._id, completedPrechatCapture),
                setShouldScrollToBottom(true),
            ]);

            actions.push(...conversationActions);
        }

        setUserForConversation(appUser, sessionToken, integrationId);

        dispatch(batchActions(actions));

        if (appUser._id) {
            subscribeFaye();
        }

        return Promise.resolve();
    };
}

let startConversationPromise;
export function startConversation(intent) {
    return (dispatch, getState) => {
        // cache and return the promise to make sure there is only one ongoing http request
        if (startConversationPromise) {
            return startConversationPromise;
        }

        const {
            user,
            config: { appId, integrationId },
            appState: { activeConversationId },
            conversations,
            pendingUserProps,
        } = getState();
        const { _id: appUserId, userId } = user;
        const { _id: conversationId } = conversations[activeConversationId];

        if (conversationId) {
            return Promise.resolve({ conversations: [conversations[activeConversationId]] });
        }

        let promise;
        if (appUserId) {
            promise = dispatch(
                http('POST', `/v2/apps/${appId}/appusers/${appUserId}/conversation`, {
                    client: getClientInfo(integrationId),
                    intent,
                }),
            ).then(({ conversation }) => {
                return {
                    conversations: [{ ...conversation, messages: [] }],
                    appUser: user,
                };
            });
        } else {
            promise = dispatch(
                http('POST', `/v2/apps/${appId}/appusers`, {
                    ...pendingUserProps,
                    userId,
                    client: getClientInfo(integrationId),
                    intent,
                }),
            );
            dispatch(resetPendingUserProps());
        }

        startConversationPromise = promise
            .then(
                (payload) => {
                    return dispatch(handleUserConversationsResponse(payload, { shouldFetchMessages: false }));
                },
                (error) => console.error(error),
            )
            .then((response) => {
                startConversationPromise = undefined;
                return Promise.resolve(response);
            });

        return startConversationPromise;
    };
}

export function loadConversation(conversationId) {
    if (!conversationId || typeof conversationId !== 'string') {
        throw new Error('Must provide conversationId');
    }

    return (dispatch, getState) => {
        const {
            user,
            config: { appId, integrationId },
        } = getState();
        const { _id: appUserId } = user;

        const data = {
            author: {
                role: 'appUser',
                appUserId,
                client: getClientInfo(integrationId),
                sessionId: getSessionId(),
            },
        };

        return dispatch(
            http('POST', `/v2/apps/${appId}/conversations/${conversationId}/subscribe`, data),
        ).then((response) => dispatch(handleUserConversationResponse(response)));
    };
}

export function fetchConversationIfNeeded(conversationId) {
    return (dispatch, getState) => {
        const { conversations } = getState();
        const targetConversation = conversations[conversationId];

        if (targetConversation && targetConversation.messagesUpdated) {
            return Promise.resolve(targetConversation);
        }

        return dispatch(getConversation(conversationId));
    };
}

export function getConversation(conversationId) {
    return (dispatch, getState) => {
        const {
            config: { appId },
        } = getState();
        return dispatch(http('GET', `/v2/apps/${appId}/conversations/${conversationId}`)).then((response) => {
            const actions = [
                setConversation({
                    ...response.conversation,
                    hasMoreMessages: !!response.hasPrevious,
                }),
                setMessages(conversationId, response.messages),
            ];

            dispatch(batchActions(actions));
            return response;
        });
    };
}

export function fetchUserConversation() {
    return (dispatch, getState) => {
        const {
            user: { _id },
            config: { appId, integrationId },
        } = getState();

        return dispatch(http('GET', `/v2/apps/${appId}/appusers/${_id}`)).then((response) => {
            verifyStoredClientId(response.appUser, integrationId);
            return dispatch(handleUserConversationsResponse(response));
        });
    };
}

export function getConversations(offset = 0) {
    return (dispatch, getState) => {
        const {
            user: { _id },
            config: { appId, integrationId },
        } = getState();

        return dispatch(http('GET', `v2/apps/${appId}/appusers/${_id}/conversations`, { offset }))
            .then((response) => {
                verifyStoredClientId(response.appUser, integrationId);

                if (response.conversations.length > 0) {
                    return dispatch(setConversations(response.conversations));
                }
            })
            .then(() => getState().conversations);
    };
}

export function startPrechatCaptureConversation(messages, formResponse) {
    return (dispatch, getState) => {
        const {
            config: { prechatCapture = {} },
            user: { userId },
            config: { appId, integrationId, integrations },
            pendingUserProps,
        } = getState();

        return dispatch(
            http('POST', `/v2/apps/${appId}/prechatcapture`, {
                ...pendingUserProps,
                messages,
                formResponse,
                userId,
                sender: {
                    type: 'appUser',
                    client: getClientInfo(integrationId),
                    sessionId: getSessionId(),
                },
            }),
        )
            .catch(() => {
                throw new Error(PRECHAT_CAPTURE_ERROR);
            })
            .then((response) => {
                return dispatch(handleUserConversationsResponse({ ...response, completedPrechatCapture: true }));
            })
            .then(() => {
                if (!prechatCapture.enableEmailLinking) {
                    return;
                }

                const mailgunIntegration = integrations.find(({ type }) => type === 'mailgun');

                if (!mailgunIntegration) {
                    return;
                }

                const { email } = formResponse.fields.find((field) => field.type === 'email') || {};

                if (!email) {
                    return;
                }

                const criteria = {
                    type: 'mailgun',
                    integrationId: mailgunIntegration._id,
                    address: email,
                };

                return dispatch(linkEmailChannel(criteria));
            })
            .then(() => {
                const {
                    appState: { activeConversationId },
                } = getState();
                return dispatch(setCompletedPrechatCaptureLinking(activeConversationId, true));
            });
    };
}

export function validateFormField({ form, formResponse, isPrechatCaptureForm = false }) {
    return (dispatch, getState) => {
        const {
            config: { appId, integrationId },
            user: { _id },
        } = getState();

        const url = isPrechatCaptureForm
            ? `/v2/apps/${appId}/prechatcapture/validate`
            : `/v2/apps/${appId}/appusers/${_id}/messages/validate`;

        return dispatch(
            http('POST', url, {
                form,
                formResponse,
                sender: {
                    type: 'appUser',
                    client: getClientInfo(integrationId),
                    sessionId: getSessionId(),
                },
            }),
        );
    };
}

/**
 * Updates the conversation list when outdated. Attempts to recover the state of the active conversation if not up-to-date.
 * @param {Object} options
 * @param {boolean} options.keepConvoWithFetchedMessages Performs a hard reset when set to `false`
 * @returns {Function} Redux action
 */

export function refreshUserConversations(options = {}) {
    const { keepConvoWithFetchedMessages = true } = options;

    return (dispatch, getState) => {
        const {
            config: { appId },
            user: { _id: appUserId },
            appState: { activeConversationId },
            conversations: currentConversations,
        } = getState();
        if (activeConversationId === 'default') {
            return dispatch(fetchUserConversation());
        }
        return dispatch(http('GET', `/v2/apps/${appId}/appusers/${appUserId}/conversations`)).then((response) => {
            const { conversations } = response;
            const currentActiveConvo = currentConversations[activeConversationId];
            const actions = [refreshConversations(conversations, { keepConvoWithFetchedMessages })];

            if (!keepConvoWithFetchedMessages || !currentActiveConvo.messagesUpdated) {
                return dispatch(_getMessages({ conversationId: activeConversationId })).then((response) => {
                    actions.push(
                        setConversation({ _id: activeConversationId, hasMoreMessages: !!response.hasPrevious }),
                    );
                    actions.push(setMessages(activeConversationId, response.messages));

                    return dispatch(batchActions(actions));
                });
            }
            return dispatch(batchActions(actions));
        });
    };
}
